import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Users, CheckCircle, Loader } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSupabase } from '../context/SupabaseContext';

interface StudentOption {
  id: string;
  name: string;
}

const BookLesson: React.FC = () => {
  const navigate = useNavigate();
  const { state, fetchStudents, bookALesson, findAvailableSlots, fetchTeachers } = useSupabase();

  const [studentsOptions, setStudentsOptions] = useState<StudentOption[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [selectedTeacherId, setSelectedTeacherId] = useState<string>();
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState(60);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [selectedSlotStart, setSelectedSlotStart] = useState<string | null>(null);

  // Load students once
  useEffect(() => {
    fetchStudents();
    fetchTeachers();
  }, []);

  useEffect(() => {
    if (date){
      // console.log("state.userRole", state.userRole)
      if(state.userRole == 'teacher') {
        findAvailableSlots(new Date(date), state.userProfile.id, new Date() );
      }
    }
  },[date]);

  useEffect(() => {
    if ( selectedTeacherId && date && selectedTeacherId != ''){
      findAvailableSlots(new Date(date), selectedTeacherId, new Date() );
    }
  },[selectedTeacherId, date]);

  useEffect(() => {
    setStudentsOptions(
      state.students.map(s => ({ id: s.id, name: s.first_name + ' ' + s.last_name }))
    );
  }, [state.students]);

  const canSubmit =
    selectedStudentId && date && time && !submitting && state.availableSlots?.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);

    try {
      const start = new Date(`${date}T${time}:00`);
      const end = new Date(start.getTime() + duration * 60000);

      bookALesson(selectedStudentId, 'Booked lesson', start, end, selectedTeacherId);

      setTimeout(() => {
        navigate('/lesson');
      }, 1200);
    } catch (err) {
      console.error('Failed to book lesson:', err);
      setSubmitting(false);
    }
  };

  return (
    <div className="fade-in max-w-md mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-1">Book a Session</h1>
        <p className="text-text-light text-sm">
          Choose student, date and time to create a new lesson.
        </p>
      </div>

      {success ? (
        <div className="card p-10 text-center">
          <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
          <p className="font-bold text-lg mb-1">Lesson booked!</p>
          <p className="text-text-light text-sm">Redirecting to agenda…</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Student select */}
          <div className="card p-4">
            <label className="text-xs uppercase tracking-wide text-text-light flex items-center gap-2 mb-2">
              <Users className="w-4 h-4" />
              Student
            </label>
            <select
              className="w-full px-3 py-2 border border-border rounded-xl bg-background"
              value={selectedStudentId}
              onChange={(e) => setSelectedStudentId(e.target.value)}
            >
              <option value="">Select a student</option>
              {studentsOptions.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          {/* Teacher select */}
          { state.userRole === 'admin' && (
            <div className="card p-4">
              <label className="text-xs uppercase tracking-wide text-text-light flex items-center gap-2 mb-2">
                <Users className="w-4 h-4" />
                Teacher
              </label>
              <select
                className="w-full px-3 py-2 border border-border rounded-xl bg-background"
                value={selectedTeacherId}
                onChange={(e) => setSelectedTeacherId(e.target.value)}
              >
                <option value="">Select a teacher</option>
                {state.teachers.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.first_name + ' ' + t.last_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Date */}
          <div className="card p-4">
            <label className="text-xs uppercase tracking-wide text-text-light flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4" />
              Date
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-border rounded-xl bg-background"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          {/* Time suggestions */}
          <div className="text-sm text-gray-500">
            { state.availableSlots?.length > 0 && date && (
              <div>
                <p className="mb-1">Available slots on this day:</p>
                <div className="flex flex-wrap gap-2">
                  {state.availableSlots?.map(slot => (
                    <button
                      key={slot.start.toISOString()}
                      type="button"
                      className={
                        "px-3 py-1 rounded-full text-xs transition-colors " +
                        (selectedSlotStart === slot.start.toISOString()
                          ? "bg-green-500 text-white" // selected green
                          : "bg-blue-100 text-blue-800 hover:bg-blue-200")
                      }
                      onClick={() => {
                        const start = new Date(slot.start);
                        const end = new Date(slot.end);
                        setSelectedSlotStart(slot.start.toISOString());
                        setTime(start.toTimeString().slice(0,5));
                        setDuration((end.getTime() - start.getTime()) / 60000);
                      }}
                    >
                      {new Date(slot.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} -
                      {new Date(slot.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </button>
                  ))}
                </div>
              </div>
            )}
            { state.availableSlots?.length == 0 && selectedTeacherId && date && (
              <>No availiability at this time</>
            )}
          </div>

          {/* Time + duration */}
          <div className="card p-4 grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wide text-text-light flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4" />
                Time
              </label>
              <input
                type="time"
                className="w-full px-3 py-2 border border-border rounded-xl bg-background"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-text-light mb-2 block">
                Duration
              </label>
              <select
                className="w-full px-3 py-2 border border-border rounded-xl bg-background"
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
              >
                <option value={30}>30 min</option>
                <option value={45}>45 min</option>
                <option value={60}>60 min</option>
                <option value={90}>90 min</option>
              </select>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full btn btn-primary py-3 flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {submitting ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Booking…
              </>
            ) : (
              'Book Session'
            )}
          </button>
        </form>
      )}
    </div>
  );
};

export default BookLesson;
